import { FormHelperText } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";

/**
 * Segmented one-time-code entry — one box per digit.
 *
 * Used for two-factor sign-in and enrolment. A single wide text field with big
 * letter-spacing looks approximately like this but behaves worse: there is no
 * indication of how many digits are expected, no per-digit caret, and pasting a
 * code copied out of an email drops it in as one unbroken string.
 *
 * Behaviour worth knowing about:
 *  - Typing auto-advances; Backspace on an empty box steps back and clears.
 *  - Pasting anywhere distributes the digits across the boxes, so a paste of
 *    "123 456" or "code: 123456" still lands correctly (non-digits are stripped).
 *  - `dir="ltr"` is pinned on the row: a code is a number, and under the Arabic
 *    RTL layout the boxes would otherwise fill right-to-left and read backwards.
 *  - Only the first box carries autoComplete="one-time-code" — repeating it makes
 *    iOS offer the SMS autofill chip on every box.
 */
export default function InputOtp({
  value = "",
  onChange,
  length = 6,
  // `error` renders a message underneath. `invalid` only paints the boxes red —
  // for screens that already show the failure message somewhere else, so the
  // error is not stated twice.
  error = null,
  invalid = false,
  disabled = false,
  autoFocus = false,
  name = "otp",
}) {
  const showInvalid = invalid || !!error;
  const refs = useRef([]);

  // The value is the single source of truth; the boxes are just a view of it.
  const chars = useMemo(() => {
    const v = (value || "").replace(/\D/g, "").slice(0, length);
    return Array.from({ length }, (_, i) => v[i] ?? "");
  }, [value, length]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  // A parent that clears the value after a rejected code is asking the user to
  // type again — put the caret back in the first box so they can, without
  // reaching for the mouse.
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current && !value) refs.current[0]?.focus();
    prev.current = value;
  }, [value]);

  const emit = (next) => onChange?.(next.join("").slice(0, length));

  const focusBox = (i) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  const handleChange = (i, raw) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;

    const next = [...chars];
    // Typing into a box mid-code, or an autofilled/pasted burst arriving through
    // a single box, both land here — spread from this position rather than
    // taking only the first character.
    for (let k = 0; k < digits.length && i + k < length; k++) {
      next[i + k] = digits[k];
    }
    emit(next);
    focusBox(i + digits.length);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...chars];
      if (next[i]) {
        next[i] = "";
        emit(next);
      } else if (i > 0) {
        next[i - 1] = "";
        emit(next);
        focusBox(i - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft") { e.preventDefault(); focusBox(i - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); focusBox(i + 1); }
    if (e.key === "Delete") {
      e.preventDefault();
      const next = [...chars];
      next[i] = "";
      emit(next);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!digits) return;
    const next = Array.from({ length }, (_, i) => digits[i] ?? "");
    emit(next);
    focusBox(digits.length);
  };

  return (
    <div>
      <div dir="ltr" className="d-flex j-center" style={{ gap: 8 }}>
        {chars.map((c, i) => (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            name={`${name}-${i}`}
            value={c}
            disabled={disabled}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={length}
            aria-label={`${name} digit ${i + 1}`}
            style={{
              width: 44,
              height: 52,
              textAlign: "center",
              fontSize: 22,
              fontWeight: 600,
              color: "#333",
              borderRadius: 8,
              outline: "none",
              caretColor: "var(--primary-color)",
              background: disabled ? "#f5f5f5" : "#fff",
              border: `1px solid ${
                showInvalid
                  ? "var(--error-color)"
                  : c
                    ? "var(--primary-color)"
                    : "#c4c4c4"
              }`,
              transition: "border-color .15s ease, box-shadow .15s ease",
            }}
            onBlur={(e) => (e.target.style.boxShadow = "none")}
            onFocusCapture={(e) =>
              (e.target.style.boxShadow = `0 0 0 2px var(--primaryXS-color)`)
            }
          />
        ))}
      </div>
      {error && (
        <FormHelperText className="error-color" style={{ textAlign: "center" }}>
          {error}
        </FormHelperText>
      )}
    </div>
  );
}
